// START IMPORTS
// Context and configuration imports
import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
// Hooks imports
import {
  usePhoneSearchData,
  useReport,
  useSeonData,
} from '@ltvco/refresh-lib/hooks';
import {
  ReactivationDaysLeft,
  uncancelBillingVariation2Value,
  uncancelBillingPbrFeatureName,
  uncancelBillingControlValue,
} from '@ltvco/refresh-lib/payments';
// Theme and UI imports
import { Box, Stack, useTheme } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  unformatPhone,
  isNeighborSpoofed,
  DateUtil,
  usePhoneFormatter,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  commonTypes,
  DebugMenu,
  ReportChangesOverview,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  useSession,
  useCreateRemouladeReportSnapshot,
  SerializedPerson,
  useMrRogersPhoneLocation,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  PhoneSection as PhoneSectionV2,
  EmailSection as EmailSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  SocialSection as SocialSectionV2,
  JobsSection as JobsSectionV2,
  EducationSection as EducationSectionV2,
  ReportRating as ReportRatingV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  NotesSection as NotesSectionV2,
  AssetsSection as AssetsSectionV2,
  PotentialOwners as PotentialOwnersV2,
  PotentialOwnersTabs,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  NavLinkDataV2,
  LizanoListItemDataPhone,
  PhoneReportOverview,
  ReportNullState,
  ReportClaimingSection,
  ReportGeneratingScreen,
  MonitoringCardV2,
  useRecentReports,
  ReportActionsWithDateUpdated,
  FraudPhoneThreatDetection,
  CommentsSection,
  usePhoneComments,
  AssociatedAccountsSection,
  getAccountsFoundCount,
  PhoneReportOverviewVariation,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import {
  useFeatureIsOn,
  useQueryClient,
  useFeatureValue,
} from '@ltvco/refresh-lib/vendors';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import {
  getPhoneNavLinkData,
  getPhoneNavLinkDataV2,
} from 'navLinkData/phoneNavLinkData';
import { isAfterTodaysCutoff } from 'utils/isAfterTodaysCutoff';

import {
  CallerIdInlineCard,
  CallerIdModal,
  CallerIdSmokeTestProvider,
  CallerIdStickyBar,
  CallerIdStickySpacer,
} from './callerIdSmokeTest';
import { getHighConfidenceDisplayData } from './utils/getHighConfidenceDisplayData';
import { getReportSections } from './utils/getReportSections';
// Asset imports

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PhoneReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'phone';
// END CONSTANTS

export function PhoneReport({
  permalink,
  firstReportScreen,
}: PhoneReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const { logError } = useContext(AppConfig);

  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;

  const isInStaggeredPhoneUpdateVariation = useFeatureIsOn('OAR-1352');
  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const isInHighConfidenceSectionsExperiment = useFeatureIsOn('RFRSH-4240');
  const isInSeonDataSectionVariation = useFeatureIsOn('RFRSH-4465');
  const isInPhoneOverviewVariation = useFeatureIsOn('RFRSH-4361');
  const uncancelFeatureValue = useFeatureValue(
    uncancelBillingPbrFeatureName,
    uncancelBillingControlValue
  );
  const uncancelFeatureIsOn = useFeatureIsOn(uncancelBillingPbrFeatureName);
  const isInOwnerTabsVariation = useFeatureIsOn('RFRSH-4692');

  const [, triggerUpdate] = useState(0);

  const createRemouladeReportSnapshot = useCreateRemouladeReportSnapshot();
  const queryClient = useQueryClient();
  const { phoneOnlyVariation } = useSubscriptionAccessConfigContext();

  const currentUserInfo = account?.account?.user_info;
  const [spam, setSpam] = useState<'unsure' | 'yes' | 'no'>('unsure');
  const zeroed = isZeroed(queryResult);
  const theme = useTheme();
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  const {
    data: phoneSearchData,
    error,
    isError,
  } = usePhoneSearchData(queryResult?.data?.meta?.search_data?.phone_number);

  const rawPhone = queryResult?.data?.meta?.search_data?.phone_number ?? '';
  const formattedPhoneNumber = usePhoneFormatter(rawPhone, true);

  const { data: seonData, isLoading: isSeonDataLoading } = useSeonData(
    { phone_number: rawPhone },
    { enabled: isInSeonDataSectionVariation }
  );
  const seonPhoneDetails = seonData?.phone_details;

  const { commentsQuery } = usePhoneComments({
    permalink,
    phoneNumber: rawPhone,
    logError,
    enabled: queryResult.isSuccess,
  });

  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [bbqReportIsReady, setIsBbqReportReady] = useState(false);
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions((prev) => ({ ...prev, ...newOptions }));
  };
  const [activeMonitor] = useState(false);
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [neighborSpoofed, setIsNeighborSpoofed] = useState(false);
  const [reportUpdateDate, setUpdateDate] = useState<string | undefined>();
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const { data: bbqPhoneData } = useRecentReports(
    { report_type: 'reverse_phone_report' },
    { enabled: bbqReportIsReady }
  );
  const phoneReports = useMemo(
    () => bbqPhoneData?.reports ?? [],
    [bbqPhoneData?.reports]
  );

  const date = new DateUtil();

  const report = useMemo(() => {
    if (!queryResult.isSuccess || !queryResult.data) return null;

    if (queryResult?.data?.entities)
      return ReportFactory.create(queryResult?.data, 'phone');
  }, [queryResult.data, queryResult.isSuccess]);

  const loading = useMemo(() => {
    return queryResult.isLoading || queryResult.isError;
  }, [queryResult.isLoading, queryResult.isError]);

  const mrRogersPhoneLocation = useMrRogersPhoneLocation(report?.phone || '');

  // END DERIVED_DATA_AND_UTILS

  // START COMPUTED_VALUES
  const hidePersonSection =
    isInStaggeredPhoneUpdateVariation &&
    isAfterTodaysCutoff(currentUserInfo?.join_date) &&
    phoneReports.length == 1;
  // END COMPUTED_VALUES

  // START EFFECTS
  useEffect(() => {
    if (isError && error) {
      const msg = error.error || error.message || '';
      if (!msg.toLowerCase().includes('not found')) {
        logError('Error fetching phone search data', error);
      }
    }
  }, [isError, error, logError]);

  useEffect(() => {
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);

      // After a 3 second delay, we expect the report that was just run to be in BBQ
      const timer = setTimeout(() => {
        queryClient.invalidateQueries(['request', 'bbq']);
        setIsBbqReportReady(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [queryResult.data, queryResult.isSuccess]);

  useEffect(() => {
    // Only run the request to remoulade if this report is the user's only phone report, or "first" report
    if (
      !hidePersonSection ||
      phoneReports.length != 1 ||
      report?.data?.people?.length === 0
    )
      return;
    createRemouladeReportSnapshot.mutate({
      permalink,
      update_type: 'phone',
    });
  }, [hidePersonSection, phoneReports]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate)
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    else if (coreUpdateDate)
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );

    if (report) {
      const spoofed = isNeighborSpoofed(
        report?.data,
        account?.account?.user_info.phone_number
      );
      setIsNeighborSpoofed(spoofed);
    }
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (firstReportScreen?.isActive) {
    return (
      <ReportGeneratingScreen
        reportType="phone"
        ready={!queryResult.isLoading}
        onComplete={() => {
          firstReportScreen?.onComplete();
          triggerUpdate((n) => n + 1);
        }}
      />
    );
  }

  if (loading || queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={15} />;
  }
  if (zeroed || (queryResult.isSuccess && !report)) {
    return <ReportNullState />;
  }

  if (!report) {
    return <ReportLoading menuItems={15} />;
  }

  const businesses = (
    report?.data?.businesses ??
    report?.data?.rawData?.entities?.businesses ??
    []
  ).filter((b) => Boolean(b.names?.[0]?.name));
  const businessName = businesses[0]?.names?.[0]?.name ?? '';
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const showReportClaiming =
    unformatPhone(currentUserInfo?.phone_number || '') === report?.phone;

  const ClaimingComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const MonitoringComponent = (
    <MonitoringCardV2 permalink={permalink} reportType={reportType} />
  );
  const isBusinessSelected =
    reportOptions.potentialOwnerIndex < businesses.length;
  const selectedBusiness = isBusinessSelected
    ? businesses[reportOptions.potentialOwnerIndex]
    : undefined;
  const selectedBusinessName = selectedBusiness?.names?.[0]?.name ?? '';

  // Combined index = businesses first, then people. Map to people[] index for getOwner.
  const selectedPersonIndex = isBusinessSelected
    ? 0
    : reportOptions.potentialOwnerIndex - businesses.length;
  const personOwner = report.getOwner({
    ...reportOptions,
    potentialOwnerIndex: selectedPersonIndex,
  });

  // Businesses are not in people[]; use their contact fields when selected.
  const owner = selectedBusiness
    ? {
        ...personOwner,
        ownerName: selectedBusinessName,
        emails: selectedBusiness.emails ?? [],
        phones: (selectedBusiness.phones ?? []).map((phone) => ({
          ...phone,
          phoneType: phone.phoneType || phone.type || undefined,
        })),
        addresses: selectedBusiness.addresses ?? [],
        educations: [],
        jobs: [],
        profiles: [],
        usernames: [],
        userIds: [],
        relatives: [],
        neighbors: [],
        associates: [],
        ownedAssets: {
          vehicles: { aircraft: [], automobiles: [], watercraft: [] },
          real_estate: [],
        },
      }
    : personOwner;

  // Remount person-based sections when the selected owner changes so list
  // content (phones, emails, addresses, etc.) always reflects that owner.
  const selectedOwnerSectionKey = `owner-${reportOptions.potentialOwnerIndex}`;
  const selectedPerson = isBusinessSelected
    ? undefined
    : report.data.people?.[selectedPersonIndex];

  const {
    ownerName,
    emails,
    educations,
    jobs,
    profiles,
    usernames,
    phones,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const highConfidenceThreshold =
    reportOptions.higherConfidenceThreshold ||
    constants.config.higherConfidenceThreshold;
  const showHighConfidenceDataOnly = reportOptions.showHighConfidenceDataOnly;
  // Skip high-confidence filtering for businesses — they lack person confidence meta.
  const { phonesForDisplay, emailsForDisplay, addressesForDisplay } =
    getHighConfidenceDisplayData({
      owner,
      showHighConfidenceDataOnly:
        !isBusinessSelected && showHighConfidenceDataOnly,
      highConfidenceThreshold,
    });
  const phoneItems =
    isInHighConfidenceSectionsExperiment && !isBusinessSelected
      ? phonesForDisplay
      : phones;
  const emailItems =
    isInHighConfidenceSectionsExperiment && !isBusinessSelected
      ? emailsForDisplay
      : emails;
  const addressItems =
    isInHighConfidenceSectionsExperiment && !isBusinessSelected
      ? addressesForDisplay
      : addresses;

  const {
    risk,
    connectivity,
    location: supportingLocation,
  } = report.supportingPhone || {};

  const {
    fraud_score,
    is_risky,
    is_recent_abuse,
    is_spammer,
    is_do_not_call,
    is_voip,
    is_prepaid,
    is_leaked,
  } = risk || {};

  const { active_status, user_activity, is_active } = connectivity || {};

  const queryParams = new URLSearchParams(search);
  const fallbackPhone = queryParams.get('phone') || '';

  // Person sections hide the searched number ("Other Phone Numbers").
  // Businesses should surface their full phone list, including the report number.
  const otherPhones = isBusinessSelected
    ? phoneItems
    : phoneItems.filter(
        (phone: commonTypes.Phone) =>
          phone.number !== fallbackPhone && phone.number !== report.phone
      );

  const selectedOwnerDisplayName = isBusinessSelected
    ? selectedBusinessName
    : ownerName || formattedPhoneNumber;

  const intlPhonePiiPolicy =
    account?.account?.subscription_info?.subscription_features
      ?.international_phone_pii_policy;
  const hideInternationalPotentialOwners =
    intlPhonePiiPolicy === 'not_included';
  const hidePotentialOwnersPII = intlPhonePiiPolicy === 'partial_included';

  let potentialOwnersNumber =
    businesses.length + (report.data.people?.length ?? 0);
  if (potentialOwnersNumber === 0 && businessName) potentialOwnersNumber = 1;
  if (isInListV2Variation) potentialOwnersNumber = 1;
  if (hideInternationalPotentialOwners) potentialOwnersNumber = 0;

  const { main_line_type, num_searches, spam_score } = report.data;
  const searchCount = phoneSearchData?.total_volume || num_searches || 0;
  const comments = commentsQuery.isSuccess ? commentsQuery.data.comments : [];
  const commentsCount = commentsQuery.isSuccess
    ? Number(commentsQuery.data.comment_count ?? comments.length)
    : 0;

  const associatedAccountsCount = isSeonDataLoading
    ? null
    : seonData != null
    ? getAccountsFoundCount(seonData.phone_details?.account_details)
    : null;

  let phoneNavLinkDataV2 = getPhoneNavLinkDataV2(
    owner,
    {
      potentialOwnersLength: potentialOwnersNumber,
      searchVolumeLength: searchCount > 0 ? 1 : 0,
      commentsLength: commentsCount,
      neighborSpoofingLength: neighborSpoofed ? 1 : 0,
      spamReportsCount: report.data.spam_reports_count ?? 0,
      associatedAccountsCount,
      isAssociatedAccountsCountLoading: isSeonDataLoading,
    },
    theme,
    isInHighConfidenceSectionsExperiment
      ? {
          phones: phoneItems,
          emails: emailItems,
          addresses: addressItems,
        }
      : undefined
  ) as NavLinkDataV2[];

  let phoneNavLinkData = getPhoneNavLinkData(
    owner,
    {
      potentialOwnersLength: potentialOwnersNumber,
      searchVolumeLength: searchCount > 0 ? 1 : 0,
      commentsLength: commentsCount,
      neighborSpoofingLength: neighborSpoofed ? 1 : 0,
      spamReportsCount: report.data.spam_reports_count ?? 0,
      associatedAccountsCount,
      isAssociatedAccountsCountLoading: isSeonDataLoading,
    },
    isInHighConfidenceSectionsExperiment
      ? {
          phones: phoneItems,
          emails: emailItems,
          addresses: addressItems,
        }
      : undefined
  );

  if (hidePotentialOwnersPII) {
    phoneNavLinkData = phoneNavLinkData.filter(
      (item) => item.sectionId !== '#education-section'
    );
    phoneNavLinkDataV2 = phoneNavLinkDataV2.map((item) => {
      if (item.childItems) {
        return {
          ...item,
          childItems: item.childItems.filter(
            (child) => child.href !== '#education-section'
          ),
        };
      }
      return item;
    });
  }

  const carrier = report.data.meta?.search_data?.carrier;

  document.title = `${formattedPhoneNumber} - BeenVerified`;

  const buildReportDataForListV2 = () => {
    const data: LizanoListItemDataPhone = {
      permalink,
      phone_number: report?.phone || '',
      type: main_line_type || '',
      carrier: carrier || '',
      area_code_location: mrRogersPhoneLocation[1] || '',
    };

    if (report?.data?.people && report?.data?.people?.length) {
      data.owners = report.data.people.map((owner: SerializedPerson) => {
        return {
          first_name: owner?.identity?.names?.[0]?.parsed?.first || '',
          last_name: owner?.identity?.names?.[0]?.parsed?.last || '',
        };
      });
    }

    return data;
  };

  const people = report.data.people ?? [];
  const potentialOwners = people;
  const businessNames = businesses.map(
    (b) => `${b.names?.[0]?.name} (Business)`
  );
  const personNames = (potentialOwners ?? []).map((p: SerializedPerson) => {
    const first = p?.identity?.names?.[0]?.parsed?.first ?? '';
    const last = p?.identity?.names?.[0]?.parsed?.last ?? '';
    return `${first} ${last}`.trim() || 'Unknown';
  });
  const potentialOwnersNames = hideInternationalPotentialOwners
    ? []
    : [...businessNames, ...personNames];

  const showOwnerTabs =
    isInOwnerTabsVariation &&
    !hideInternationalPotentialOwners &&
    potentialOwnersNames.length > 1;

  const overviewPotentialOwners =
    showOwnerTabs && potentialOwnersNames.length > 1
      ? [
          potentialOwnersNames[reportOptions.potentialOwnerIndex] ??
            potentialOwnersNames[0],
          ...potentialOwnersNames.filter(
            (_, index) => index !== reportOptions.potentialOwnerIndex
          ),
        ]
      : potentialOwnersNames;

  const alternativeTitle = isPhonePartialAddress
    ? 'Possible Locations'
    : undefined;

  const internationalPhoneNumber = (rawPhone as string).startsWith('+');
  const supportingPhone = queryResult?.data?.supporting?.phones?.[0];

  const hideOpenReportCTA = internationalPhoneNumber || phoneOnlyVariation;

  const fallbackInfo = internationalPhoneNumber
    ? {
        address: supportingPhone?.location?.address || undefined,
        phone: formattedPhoneNumber || undefined,
      }
    : undefined;
  const phoneOverviewItemsData = {
    comments,
    commentsCount,
    mainLineType: main_line_type,
    numSearches: searchCount,
    spamScore: spam_score,
    carrier,
    peopleCount: potentialOwnersNumber,
    fraudScore: fraud_score ?? 0,
    isRisky: is_risky,
    isDoNotCall: is_do_not_call ?? false,
    isVOIP: is_voip ?? false,
    isPrepaid: is_prepaid ?? false,
    isValid: report.supportingPhone?.is_valid ?? false,
    isActive: is_active ?? false,
    isLeaked: is_leaked ?? false,
    isSpammer: is_spammer ?? false,
    isRecentAbuse: is_recent_abuse ?? false,
    activeStatus: active_status ?? 'Unknown',
    userActivity: user_activity ?? 'Unknown',
    timezone: supportingLocation?.timezone ?? '',
    potentialOwners: overviewPotentialOwners,
    parsedLocation: supportingLocation?.address?.parsed,
    location: supportingLocation?.address?.full ?? '',
  };

  if (internationalPhoneNumber) {
    (phoneNavLinkDataV2 as NavLinkDataV2[]).forEach((item) => {
      if (item?.childItems) {
        item.childItems = item.childItems.filter(
          (child) => child.href !== '#related-reports-section'
        );
      }
    });
  }

  if (hideInternationalPotentialOwners) {
    // removes the whole "Potential Owners" group if the international phone PII policy is not_included
    phoneNavLinkDataV2 = phoneNavLinkDataV2.filter(
      (item) =>
        !item.childItems?.some(
          (child) => child.href === '#potential-owners-section'
        )
    );

    phoneNavLinkData = phoneNavLinkData.filter(
      (item) =>
        item.href !== '#potential-owners-section' &&
        item.href !== '#others-associated-section'
    );
  }

  const reportSections = getReportSections(phoneNavLinkDataV2);
  // END DATA_PREPARATION

  const potentialOwnersSection = !hideInternationalPotentialOwners ? (
    <PotentialOwnersV2
      potentialOwners={potentialOwners || []}
      potentialOwnerIndex={reportOptions.potentialOwnerIndex}
      setPotentialOwnerIndex={(index: number) => {
        handleSetReportOptions({ potentialOwnerIndex: index });
      }}
      personName={selectedOwnerDisplayName}
      permalink={permalink}
      businesses={businesses}
      showHighConfidenceDataOnly={reportOptions.showHighConfidenceDataOnly}
      setShowHighConfidenceDataOnly={(value: boolean) => {
        handleSetReportOptions({
          showHighConfidenceDataOnly: value,
        });
      }}
      hidePersonSection={hidePersonSection}
      reportType={reportType}
      hideOpenReportCTA={hideOpenReportCTA}
      fallbackInfo={fallbackInfo}
      isPhoneOnlyVariation={phoneOnlyVariation}
      hidePII={hidePotentialOwnersPII}
      reportSections={reportSections}
      hideOwnerSelector={showOwnerTabs}
    />
  ) : null;

  const reportActionsContent = (
    <ReportActionsWithDateUpdated
      reportType={reportType}
      reportTitle="Phone"
      dateUpdated={reportUpdateDate}
      permalink={permalink}
      navLinkData={phoneNavLinkDataV2 as unknown as NavLinkDataV2[]}
      reportUpgraded={false}
      reportDataForListV2={
        isInListV2Variation ? buildReportDataForListV2() : undefined
      }
    />
  );

  // START RENDER
  return (
    <CallerIdSmokeTestProvider pageType="phone_report" permalink={permalink}>
      <Box>
        <Stack direction="row">
          {/* START DESKTOP_NAVIGATION */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
            }}
          >
            <ReportNavigationMenu
              reportType="Phone Report"
              headerTitle={formattedPhoneNumber}
              menuItems={
                phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
              }
            />
          </Box>
          {/* END DESKTOP_NAVIGATION */}

          {/* START MAIN_CONTENT */}
          <Box
            marginTop={0}
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {/* START REPORT_CHANGES_OVERVIEW */}
            {activeMonitor && (
              <ReportChangesOverview
                permalink={permalink}
                navLinkData={phoneNavLinkData}
              />
            )}
            {/* END REPORT_CHANGES_OVERVIEW */}

            {/* START REPORT_ACTIONS */}
            <Box
              sx={{
                height: 30,
                backgroundColor: '#f8f8f8',
                position: 'sticky',
                marginBottom: '-10px',
                marginX: -1,
                top: 52,
                zIndex: 10,
                display: {
                  xs: 'none',
                  sm: 'block',
                  md: 'block',
                  lg: 'block',
                },
              }}
            />
            {uncancelFeatureIsOn &&
              uncancelFeatureValue === uncancelBillingVariation2Value && (
                <ReactivationDaysLeft
                  direction="row"
                  isInReport
                  customPadding={'16px 0 16px 0px'}
                />
              )}
            {/* END REPORT_ACTIONS */}

            {!showOwnerTabs && reportActionsContent}

            {showOwnerTabs && (
              <>
                <PotentialOwnersTabs
                  potentialOwners={potentialOwners || []}
                  businesses={businesses}
                  potentialOwnerIndex={reportOptions.potentialOwnerIndex}
                  setPotentialOwnerIndex={(index: number) => {
                    handleSetReportOptions({ potentialOwnerIndex: index });
                  }}
                  reportType={reportType}
                />
                {potentialOwnersSection}
              </>
            )}

            {showOwnerTabs && reportActionsContent}

            {/* START REPORT_OVERVIEW */}

            {isInPhoneOverviewVariation ? (
              <PhoneReportOverviewVariation
                dateUpdated={reportUpdateDate}
                reportType={reportType}
                phone={formattedPhoneNumber}
                personName={selectedOwnerDisplayName}
                neighborSpoofed={neighborSpoofed}
                PhoneOverviewVariationItemsData={{
                  commentsCount,
                  comments,
                  mainLineType: main_line_type,
                  numSearches: searchCount,
                  spamScore: spam_score,
                  carrier,
                  peopleCount: potentialOwnersNumber,
                  fraudScore: fraud_score ?? 0,
                  isRisky: is_risky,
                  isDoNotCall: is_do_not_call ?? false,
                  isVOIP: is_voip ?? false,
                  isPrepaid: is_prepaid ?? false,
                  isValid: report.supportingPhone?.is_valid ?? false,
                  isActive: is_active ?? false,
                  isLeaked: is_leaked ?? false,
                  isSpammer: is_spammer ?? false,
                  isRecentAbuse: is_recent_abuse ?? false,
                  activeStatus: active_status ?? 'Unknown',
                  userActivity: user_activity ?? 'Unknown',
                  timezone: supportingLocation?.timezone ?? '',
                  potentialOwners: overviewPotentialOwners,
                  parsedLocation: supportingLocation?.address?.parsed,
                  location: supportingLocation?.address?.full ?? '',
                }}
                Monitoring={MonitoringComponent}
                showHighConfidenceToggle={false}
                showHighConfidenceDataOnly={
                  reportOptions.showHighConfidenceDataOnly
                }
                setShowHighConfidenceDataOnly={(value: boolean) => {
                  handleSetReportOptions({ showHighConfidenceDataOnly: value });
                }}
                useThreatDetectionSection
                internationalPhoneNumber={internationalPhoneNumber}
                potentialOwner={selectedPerson}
                fallbackInfo={fallbackInfo}
              />
            ) : (
              <PhoneReportOverview
                dateUpdated={reportUpdateDate}
                reportType={reportType}
                phone={formattedPhoneNumber}
                personName={selectedOwnerDisplayName}
                neighborSpoofed={neighborSpoofed}
                phoneOverviewItemsData={phoneOverviewItemsData}
                Monitoring={MonitoringComponent}
                showHighConfidenceToggle={false}
                showHighConfidenceDataOnly={
                  reportOptions.showHighConfidenceDataOnly
                }
                setShowHighConfidenceDataOnly={(value: boolean) => {
                  handleSetReportOptions({ showHighConfidenceDataOnly: value });
                }}
                useThreatDetectionSection
                internationalPhoneNumber={internationalPhoneNumber}
                potentialOwner={selectedPerson}
                fallbackInfo={fallbackInfo}
              />
            )}
            {/* END REPORT_OVERVIEW */}

            {ClaimingComponent}

            {/* START PERSONAL_INFO_SECTIONS */}
            <>
              {!hideInternationalPotentialOwners && (
                <>
                  {!showOwnerTabs && potentialOwnersSection}

                  <Fragment key={selectedOwnerSectionKey}>
                    <PhoneSectionV2
                      personName={selectedOwnerDisplayName}
                      phoneList={otherPhones}
                      permalink={permalink}
                      alternateTitle="Other Phone Numbers"
                    />
                    <EmailSectionV2
                      personName={selectedOwnerDisplayName}
                      emailList={emailItems}
                      permalink={permalink}
                      openEmailReportCTA={!internationalPhoneNumber}
                    />
                    {!phoneOnlyVariation && (
                      <AddressHistorySectionV2
                        addresses={addressItems}
                        personName={selectedOwnerDisplayName}
                        permalink={permalink}
                        realEstate={ownedAssets?.real_estate}
                        alternateTitle={alternativeTitle}
                        openReportCTA={!internationalPhoneNumber}
                        enableCardClick={!internationalPhoneNumber}
                      />
                    )}
                    <SocialSectionV2
                      personName={selectedOwnerDisplayName}
                      profiles={profiles}
                      usernames={usernames}
                      permalink={permalink}
                      openSocialReportCTA={!internationalPhoneNumber}
                    />
                    <JobsSectionV2
                      personName={selectedOwnerDisplayName}
                      jobs={jobs}
                      permalink={permalink}
                    />
                    {!hidePotentialOwnersPII && (
                      <EducationSectionV2
                        personName={selectedOwnerDisplayName}
                        educations={educations}
                        permalink={permalink}
                      />
                    )}
                    {!phoneOnlyVariation && (
                      <AssetsSectionV2
                        personName={selectedOwnerDisplayName}
                        ownedAssets={ownedAssets}
                        permalink={permalink}
                      />
                    )}
                  </Fragment>
                </>
              )}
            </>
            {/* END PERSONAL_INFO_SECTIONS */}

            {isInSeonDataSectionVariation && (
              <AssociatedAccountsSection
                personName={selectedOwnerDisplayName}
                phoneDetails={seonPhoneDetails}
                isLoading={isSeonDataLoading}
              />
            )}

            {/* START FRAUD_SCAN_SECTION */}
            <FraudPhoneThreatDetection
              isOnPhoneReport
              fraudScore={fraud_score ?? 0}
              isRisky={is_risky}
              isRecentAbuse={is_recent_abuse}
              isSpammer={is_spammer}
              searchCount={searchCount}
              spamScore={report?.data?.spam_score}
              spamReportsCount={report?.data?.spam_reports_count}
              userComments={comments?.length}
              permalink={permalink}
              spam={spam}
              setSpam={setSpam}
            />
            {/* END FRAUD_SCAN_SECTION */}

            <CallerIdInlineCard />

            {/* START ADDITIONAL_INFO_SECTIONS */}

            <CommentsSection
              commentsCount={commentsCount}
              comments={commentsQuery.data?.comments ?? []}
              reportData={report.data}
              permalink={permalink}
              formattedPhoneNumber={formattedPhoneNumber}
              reportType={reportType}
            />

            <NotesSectionV2 permalink={permalink} />

            {!internationalPhoneNumber && !hideInternationalPotentialOwners && (
              <RelatedReportsSectionV2
                key={`related-${selectedOwnerSectionKey}`}
                reportType="phone"
                personName={selectedOwnerDisplayName}
                potentialOwners={people}
                relatives={relatives}
                addresses={addresses}
                phones={phones}
                emails={emails}
                usernames={usernames}
                hidePersonSection={hidePersonSection}
              />
            )}

            <ReportRatingV2
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
            <CallerIdStickySpacer />
            {/* END ADDITIONAL_INFO_SECTIONS */}
          </Box>
          {/* END MAIN_CONTENT */}
        </Stack>

        {/* START MOBILE_NAVIGATION */}
        <ReportNavigationMobileMenu
          reportType="Phone Report"
          headerTitle={formattedPhoneNumber}
          menuItems={
            phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
          }
        />
        {/* END MOBILE_NAVIGATION */}

        {/* START DEBUG_MENU */}
        <DebugMenu menuItems={report.data.rawData.debug_menu} />
        {/* END DEBUG_MENU */}
        <CallerIdStickyBar />
        <CallerIdModal />
      </Box>
    </CallerIdSmokeTestProvider>
  );
  // END RENDER
}
